/**
 * Canonical rich-text HTML allow-list — single source of truth for both the
 * client (DOMPurify) and server (sanitize-html) sanitizers so they can't drift.
 */

// Includes legacy inline tags (b/i/del/strike/mark/sub/sup): mark/sub/sup have
// no installed extension, so stripping them would permanently destroy formatting.
export const RICH_TEXT_ALLOWED_TAGS = [
  "p",
  "br",
  "h1",
  "h2",
  "h3",
  "h4",
  "ul",
  "ol",
  "li",
  "blockquote",
  "pre",
  "code",
  "strong",
  "em",
  "u",
  "s",
  "a",
  "span",
  "hr",
  "img",
  "table",
  "thead",
  "tbody",
  "tr",
  "th",
  "td",
  "input",
  "label",
  "div",
  "b",
  "i",
  "del",
  "strike",
  "mark",
  "sub",
  "sup",
] as const;

export const RICH_TEXT_GLOBAL_ATTRS = [
  "data-id",
  "data-type",
  "data-label",
  "data-workflow-id",
  "data-mention-suggestion-char",
  "data-indent",
  // TaskItem checkbox state round-trips ONLY here, never via the inner <input checked>.
  "data-checked",
  "class",
  "style",
] as const;

export const RICH_TEXT_TAG_ATTRS: Readonly<Record<string, readonly string[]>> = {
  a: ["href", "target", "rel"],
  img: ["src", "alt"],
  td: ["colspan", "rowspan"],
  th: ["colspan", "rowspan"],
  input: ["type", "checked"],
};

export const RICH_TEXT_TEXT_ALIGN_VALUES = ["left", "center", "right", "justify"] as const;

export function stripHtmlToText(html: string | null | undefined): string {
  if (!html) return "";
  return String(html)
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    // `&amp;` LAST: decoding it first would re-decode the output above, turning
    // stored `&amp;lt;` (the chars "&lt;") into "<" instead of leaving "&lt;".
    .replace(/&amp;/g, "&")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Plain text → rich-text HTML: escapes markup characters and wraps each
 * newline-separated line in a paragraph, matching the editor's document shape.
 * The escaping is the safety contract — callers interpolate untrusted values
 * (e.g. automation variables) into the text BEFORE converting, so a value can
 * never smuggle live markup into a rich-text surface.
 */
export function plainTextToHtml(text: string | null | undefined): string {
  if (!text) return "";
  return String(text)
    .split("\n")
    .map(
      (line) =>
        `<p>${line.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</p>`,
    )
    .join("");
}

const SAFE_COLOR_RE = /^(#[0-9a-f]{3,8}|rgba?\([\d.,\s%]+\)|hsla?\([\d.,\s%]+\))$/i;

export function sanitizeStyleAttribute(style: string): string {
  const kept: string[] = [];
  for (const decl of style.split(";")) {
    const [rawProp, ...rest] = decl.split(":");
    const prop = rawProp?.trim().toLowerCase();
    const value = rest.join(":").trim().toLowerCase();
    if (!prop || !value) continue;
    if (prop === "text-align" && (RICH_TEXT_TEXT_ALIGN_VALUES as readonly string[]).includes(value)) {
      kept.push(`text-align: ${value}`);
    } else if (prop === "color" && SAFE_COLOR_RE.test(value)) {
      kept.push(`color: ${value}`);
    }
  }
  return kept.join("; ");
}
